import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Card, Btn } from "../../components/UI";
import { FLabel, FInput, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { ClientSelect, useClients } from "./shared";
import ClinicalRecordLockControls from "../../components/ClinicalRecordLockControls";
import { SignModal } from "../../components/SignatureCanvas";
import { SignatureStamp } from "../../components/SignaturePad";
import { useAuth } from "../../context/AuthContext";
import { ArrowLeft, Lock, ShieldAlert, PenLine, FileText, Download } from "lucide-react";
import { useSaveShortcut, SAVE_HINT } from "../../lib/useSaveShortcut";

const EMPTY = {
  client_id: "", title: "Crisis or Safety Plan",
  plan_date: dayjs().format("YYYY-MM-DD"),
  warning_signs: "", coping_strategies: "", social_distractions: "",
  support_contacts: "", professionals: "988 Suicide and Crisis Lifeline (call or text 988)",
  safe_environment: "", reasons_for_living: "", medications_crisis: "",
  de_escalation: "", notes: "",
};

const AMEND_FIELDS = [
  { key: "title", label: "Title" },
  { key: "warning_signs", label: "Step 1: Warning signs", type: "textarea", rows: 3 },
  { key: "coping_strategies", label: "Step 2: Internal coping strategies", type: "textarea", rows: 3 },
  { key: "social_distractions", label: "Step 3: People and settings that distract", type: "textarea", rows: 2 },
  { key: "support_contacts", label: "Step 4: People to ask for help", type: "textarea", rows: 2 },
  { key: "professionals", label: "Step 5: Professionals or agencies to contact", type: "textarea", rows: 2 },
  { key: "safe_environment", label: "Step 6: Making the environment safe", type: "textarea", rows: 2 },
  { key: "reasons_for_living", label: "Reasons for living", type: "textarea", rows: 2 },
  { key: "medications_crisis", label: "Crisis medications / PRN", type: "textarea", rows: 2 },
  { key: "de_escalation", label: "Preferred de-escalation strategies", type: "textarea", rows: 2 },
  { key: "notes", label: "Additional notes", type: "textarea", rows: 2 },
];

export default function CrisisPlanEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || { url: "/clinical/crisis-plans", label: "Crisis Plans" };
  const goBack = () => navigate(returnTo.url);
  const clients = useClients();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);

  useEffect(() => {
    const preselect = searchParams.get("client");
    if (preselect && isNew) setForm((f) => ({ ...f, client_id: preselect }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    if (isNew) return;
    api.get(`/crisis-plans/${id}`).then((r) => { setPlan(r.data); setForm({ ...EMPTY, ...r.data }); })
      .catch(() => { toast.error("Crisis plan not found"); navigate("/clinical/crisis-plans"); });
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = !!plan?.locked;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const defaultSignerName = user?.signature_name ||
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.email || "";

  useSaveShortcut(() => { if (!busy) save(); }, !locked);
  const save = async () => {
    if (!form.client_id) { toast.error("Select a client"); return null; }
    if (!(form.warning_signs || "").trim() && !(form.coping_strategies || "").trim()) {
      toast.error("Fill Step 1 (warning signs) or Step 2 (coping strategies) before saving");
      return null;
    }
    setBusy(true);
    try {
      let r;
      if (isNew) r = await api.post(`/clients/${form.client_id}/crisis-plans`, form);
      else r = await api.put(`/crisis-plans/${id}`, form);
      toast.success("Crisis plan saved");
      if (isNew) navigate(`/clinical/crisis-plans/${r.data.id}`, { replace: true });
      else { setPlan(r.data); setForm({ ...EMPTY, ...r.data }); }
      setBusy(false);
      return r.data;
    } catch (err) { toast.error(apiError(err)); setBusy(false); return null; }
  };

  const openSignFlow = () => {
    // Always open the signature modal so the audit trail captures a fresh method and
    // ESIGN consent per sign event, per Flynaut directive and 21 CFR Part 11.
    setSignOpen(true);
  };

  const submitSignature = async ({ name, image }) => {
    // Ensure a plan record exists before signing (autosave draft if new)
    let target = plan;
    if (!target?.id) {
      const saved = await save();
      if (!saved) return;
      target = saved;
    }
    setBusy(true);
    try {
      // Persist any last-minute edits before sign
      if (!locked) await api.put(`/crisis-plans/${target.id}`, form);
      const r = await api.post(`/crisis-plans/${target.id}/sign`, { name, image });
      setPlan(r.data); setForm({ ...EMPTY, ...r.data });
      setSignOpen(false);
      toast.success("Crisis plan signed and locked. Full signature audit embedded.");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const openRender = async (download) => {
    if (!plan?.id) return;
    try {
      const params = download ? { download: 1 } : {};
      if (download) {
        const reason = window.prompt(
          "Business reason for downloading PHI (required):\n\n" +
          "Type one of: continuity of care, client request, legal\n" +
          "or a short free-text explanation.",
          "continuity of care"
        );
        if (!reason || !reason.trim()) {
          toast.error("Download cancelled. A business reason is required.");
          return;
        }
        params.business_reason = reason.trim();
      }
      const r = await api.get(`/crisis-plans/${plan.id}/render`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Pop-up blocked. Allow pop-ups for this site to view the document.");
      }
      // Revoke after 60s so the tab has time to render (browsers typically hold the reference)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(apiError(err)); }
  };

  const canSign = !!plan?.id && !locked;

  return (
    <div data-testid="crisis-plan-editor">
      <button data-testid="cp-back-btn" onClick={goBack}
        className="mb-3 flex items-center gap-1 text-sm text-[#7B6157] hover:underline">
        <ArrowLeft size={15} /> Back to {returnTo.label}
      </button>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">
          <ShieldAlert size={18} className="mr-1.5 inline-block text-[#8D5A00]" />
          {isNew ? "New Crisis or Safety Plan" : `${form.title || "Crisis Plan"} - ${plan?.client_name || ""}`}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {locked && (
            <span data-testid="cp-locked-banner"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5E9] px-3.5 py-1.5 text-xs font-bold text-[#2E7D32]">
              <Lock size={12} /> Signed and Locked
            </span>
          )}
          {!isNew && plan && (
            <>
              <Btn data-testid="cp-view-doc-btn" variant="outline" onClick={() => openRender(false)}>
                <FileText size={13} /> View Document
              </Btn>
              <Btn data-testid="cp-download-doc-btn" variant="outline" onClick={() => openRender(true)}>
                <Download size={13} /> Download
              </Btn>
              <ClinicalRecordLockControls kind="crisis_plan" record={plan} onChange={load}
                amendableFields={AMEND_FIELDS} />
            </>
          )}
        </div>
      </div>

      <Card className="p-6 space-y-5">
        <div className="rounded-xl border border-[#EDE7DC] bg-[#FDFCF8] p-3 text-xs text-[#5D4037]">
          <div className="font-semibold text-[#4E342E]">Stanley-Brown Safety Plan structure</div>
          <div className="mt-1">Steps 1 through 6 walk the client from warning signs to help-seeking, with reasons for living and means-safety at the end. Once signed, the plan is locked and a full E-Signature audit block (Signature ID, Document ID, SHA-256 hash, timestamp, and IP) is embedded. Amendments are logged with author, timestamp, and change summary.</div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <FLabel>Client</FLabel>
            <ClientSelect clients={clients} value={form.client_id}
              onChange={(v) => setField("client_id", v)} testId="cp-client-select"
              disabled={!isNew || locked} />
          </div>
          <div className="sm:col-span-1">
            <FLabel>Plan Title</FLabel>
            <FInput data-testid="cp-title" value={form.title}
              onChange={(e) => setField("title", e.target.value)} disabled={locked} />
          </div>
          <div className="sm:col-span-1">
            <FLabel>Plan Date</FLabel>
            <DateField testId="cp-plan-date" value={form.plan_date}
              onChange={(v) => setField("plan_date", v)} disabled={locked} />
          </div>
        </div>

        <div>
          <FLabel>Step 1 - Warning signs (thoughts, images, moods, situations)</FLabel>
          <FTextarea data-testid="cp-warning-signs" rows={3} value={form.warning_signs}
            onChange={(e) => setField("warning_signs", e.target.value)} disabled={locked} />
        </div>
        <div>
          <FLabel>Step 2 - Internal coping strategies (things I can do to take my mind off my problems without contacting another person)</FLabel>
          <FTextarea data-testid="cp-coping-strategies" rows={3} value={form.coping_strategies}
            onChange={(e) => setField("coping_strategies", e.target.value)} disabled={locked} />
        </div>
        <div>
          <FLabel>Step 3 - People and social settings that provide distraction</FLabel>
          <FTextarea data-testid="cp-social-distractions" rows={2} value={form.social_distractions}
            onChange={(e) => setField("social_distractions", e.target.value)} disabled={locked} />
        </div>
        <div>
          <FLabel>Step 4 - People I can ask for help</FLabel>
          <FTextarea data-testid="cp-support-contacts" rows={2} value={form.support_contacts}
            onChange={(e) => setField("support_contacts", e.target.value)} disabled={locked} />
        </div>
        <div>
          <FLabel>Step 5 - Professionals or agencies to contact in crisis (988 / GCAL 1-800-715-4225)</FLabel>
          <FTextarea data-testid="cp-professionals" rows={2} value={form.professionals}
            onChange={(e) => setField("professionals", e.target.value)} disabled={locked} />
        </div>
        <div>
          <FLabel>Step 6 - Making the environment safe (means restriction)</FLabel>
          <FTextarea data-testid="cp-safe-environment" rows={2} value={form.safe_environment}
            onChange={(e) => setField("safe_environment", e.target.value)} disabled={locked} />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <FLabel>Reasons for living</FLabel>
            <FTextarea data-testid="cp-reasons-for-living" rows={2} value={form.reasons_for_living}
              onChange={(e) => setField("reasons_for_living", e.target.value)} disabled={locked} />
          </div>
          <div>
            <FLabel>Crisis medications / PRN</FLabel>
            <FTextarea data-testid="cp-medications-crisis" rows={2} value={form.medications_crisis}
              onChange={(e) => setField("medications_crisis", e.target.value)} disabled={locked} />
          </div>
        </div>
        <div>
          <FLabel>Preferred de-escalation strategies</FLabel>
          <FTextarea data-testid="cp-de-escalation" rows={2} value={form.de_escalation}
            onChange={(e) => setField("de_escalation", e.target.value)} disabled={locked} />
        </div>
        <div>
          <FLabel>Additional notes</FLabel>
          <FTextarea data-testid="cp-notes" rows={2} value={form.notes}
            onChange={(e) => setField("notes", e.target.value)} disabled={locked} />
        </div>

        {/* Embedded signature audit block once signed */}
        {plan?.signature?.signature_id && (
          <div data-testid="cp-signature-audit">
            <SignatureStamp sig={{ ...plan.signature, cosigned_by: plan.cosigned_by }} label="Verified E-Signature" />
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-[#F1EEE7] pt-4">
          {plan?.signed_at && !plan?.signature?.signature_id && (
            <div className="mr-auto text-xs text-[#7B6157]">
              Signed by <strong>{plan.signed_by_name}</strong> on {dayjs(plan.signed_at).format("MMM D, YYYY h:mm A")}
            </div>
          )}
          <Btn variant="outline" onClick={() => navigate("/clinical/crisis-plans")}>Cancel</Btn>
          <Btn data-testid="cp-save-btn" onClick={save} disabled={busy || locked} title={`Save (${SAVE_HINT})`} aria-keyshortcuts="Control+S">
            {busy ? "Saving…" : isNew ? "Save Draft" : "Save"}
          </Btn>
          {canSign && (
            <Btn data-testid="cp-sign-btn" onClick={openSignFlow} disabled={busy}
              className="border-[#2E7D32] bg-[#2E7D32] text-white hover:bg-[#1B5E20]">
              <PenLine size={13} /> Sign and Lock
            </Btn>
          )}
        </div>
      </Card>

      <SignModal open={signOpen} onClose={() => setSignOpen(false)} onSign={submitSignature}
        busy={busy}
        title="Sign Crisis or Safety Plan"
        defaultName={defaultSignerName} />
    </div>
  );
}
